<template>
  <modal :is-active="true" @close="close" :title="modalTitle">
    <template #body>
      <div class="form-horizontal" style="min-height: 480px;">
        <div v-if="scheme" class="form-group row">
          <div class="col text-center">
            <img :src="scheme.public_path" style="height: 200px; width: auto;">
          </div>
        </div>

        <div class="form-group">
          <label for="booth_refinement" class="control-label">
            {{ (mandatoryMode) ? $t('configurator.form.labels.surface') : $t('configurator.form.labels.choose_surface') }}
          </label>
          <div class="row">
            <div class="col-sm-12">
              <multiselect
                :placeholder="$t('configurator.form.placeholders.please_select')"
                :label="(!inEnglish) ? 'name' : 'name_en'"
                track-by="id"
                :options="availableRefinements"
                :show-labels="false"
                :multiple="true"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :taggable="true"
                v-model="form.refinements">
              </multiselect>
              <p v-if="form.errors.has('booth_refinement')" class="text-danger">
                {{ $t('configurator.form.errors.graphic_design') }}
              </p>
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="row">
            <div class="col-sm-12 d-flex">
              <input type="checkbox"             
                class="w-fit my-auto"
                name="is_seamless_print" 
                id="is_seamless_print"
                :disabled="form.refinements.length < 2" 
                v-model="form.is_seamless_print">
              <label for="is_seamless_print" class="control-label ml-2 my-auto">
                {{ $t('configurator.form.labels.seamless_print_with_note') }}
              </label>
            </div>
              <span v-if="form.errors.has('is_seamless_print')" class="help-block">
                <strong>{{ form.errors.get('is_seamless_print') }}</strong>
              </span>
          </div>
        </div>

        <div class="form-group">
          <label for="article_id" class="control-label">{{ $t('configurator.form.labels.print_article') }}:</label>
          <div class="row">
            <div class="col-sm-12">
              <multiselect v-model="form.article"
                :placeholder="printArticlePlaceholder"
                label="name"
                track-by="id"
                :options="availableArticles"
                :option-height="104"
                :custom-label="customLabel"
                :show-labels="false"
                :disabled="form.refinements.length === 0">
                <template #singleLabel="props">
                  <span class="booth-draft__font">{{ !inEnglish ? props.option.article.name : props.option.article.name_en }}</span>
                </template>
                <template #option="props">
                  <booth-refinement-print-article-option
                    :article="props.option.article"
                    :is-configurator="configurator">
                  </booth-refinement-print-article-option>
                </template>
                <template #noOptions>
                  <span v-if="availableArticles.length > 0">
                    {{ $t('configurator.form.labels.no_options') }}
                  </span>
                  <span v-else>
                    {{ $t('configurator.form.labels.no_options_last_minute') }}
                  </span>
                </template>
              </multiselect>
              <p v-if="form.errors.has('article')" class="text-danger">
                {{ $t('configurator.form.errors.print_article') }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="!form.is_logo_print && form.article !== null" class="form-group">
          <label class="control-label" for="label">
            {{ $t('configurator.form.labels.lettering') }}:
          </label>
          <input
            id="label"
            key="input-label"
            class="form-control input-sm"
            type="text"
            name="label"
            v-model.trim="form.label">
          <p v-if="form.errors.has('label')" class="text-danger">
            {{ $t('configurator.form.errors.lettering') }}
          </p>
        </div>
        <div v-show="form.is_logo_print && form.article !== null" class="form-group">
          <p>Ihre Druckdateien können Sie später in einem gesonderten Bereich, der DruckCloud, hochladen.</p>
        </div>

        <div class="form-group text-center">
          <button type="button" class="btn btn-warning mr-2" @click.prevent="close">
            {{ $t('configurator.buttons.cancel') }}
          </button>
          <button type="button" class="btn btn-primary" @click.prevent="submit">
            {{ $t('configurator.buttons.add') }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup>
import Form from '@/classes/Form'
import { ARTICLE_TYPE_PRINT, ARTICLE_TYPE_LETTERING, UNIT_SQM, UNIT_PC } from '@/constants'
import { hideLoadingIndicator, showErrorToast, showLoadingIndicator, showWarningToast } from '@/utils/toast'
import { computed, watch, reactive, ref, onMounted, inject } from 'vue'
import { useMainStore } from '@/store'
import { useI18n } from 'vue-i18n'
import FormDataForm from '@/classes/FormDataForm'
import Multiselect from 'vue-multiselect'
import Logger from '@/services/logging'
import { filterAvailableBoothRefinementArticles } from '@/utils/boothRefinements'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
/** @type {Logger} */
const logger = inject('logger', null)
// define props
const props = defineProps({
  availableRefinements: {
    type: Array,
    default: [],
  },
  booth: {
    type: Object,
    required: true
  },
  form: {
    type: Form,
    required: true,
  },
  mandatoryMode: {
    type: Boolean,
    default: false
  },
  configurator: {
    type: Boolean,
    default: false
  },
  boothType: {
    type: String,
    required: true,
  },
  width: {
    required: true
  },
  depth: {
    required: true
  },
})
// define emits
const emit = defineEmits(['close', 'submit'])
// define reactive props
const dropzoneFile = ref('')
const uploadForm = reactive(new FormDataForm({
  file: null,
}))
// define computed
const locale = computed(() => store.settings.locale)
const exhibition = computed(() => store.tmp.exhibition_slug)
const disabledBoothRefinementArticleIds = computed(() => store.availability.disabled_booth_refinement_ids)
const bookableArticleIds = computed(() => store.availability.bookable_article_ids)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const scheme = computed(() => {
  if (props.booth.schemes.length === 0) {
    return null
  }

  const matchingScheme = props.booth.schemes
    .filter((scheme) => {
      return scheme.booth_type === props.boothType
    })
    .find((scheme) => {
      return (
        parseFloat(scheme.width) === parseFloat(props.width)
        && parseFloat(scheme.depth) === parseFloat(props.depth)
      )
    })

  return (matchingScheme === undefined) ? null : matchingScheme
})

const availableArticles = computed(() => {
  if (props.form.refinements.length === 0) {
    return []
  }

  const isSeamlessPrint = (props.form.is_seamless_print) ? 1 : 0
  const data = props.form.refinements
    .map((refinement) => refinement.articles
      .filter((article) => article.is_seamless_print === isSeamlessPrint)
      .map((article) => article.article_id)
    )

  const intersection = data.reduce((a, b) => a.filter(c => b.includes(c)))

  let articles = []
  props.form.refinements.forEach((refinement) => {
    articles = articles.concat(refinement.articles.filter((article) => intersection.includes(article.article_id)))
  })
  
  articles = filterAvailableBoothRefinementArticles(
    articles,
    disabledBoothRefinementArticleIds.value,
    bookableArticleIds.value,
    isSeamlessPrint
  )

  return [
    ...new Map(articles.map((item) => [item.article_id, item])).values(),
  ]
})

const modalTitle = computed(() => {
  return (props.mandatoryMode)
    ? t('configurator.modals.titles.add_lettering')
    : t('configurator.modals.titles.add_graphic_design')
})

const printArticlePlaceholder = computed(() => {
  return (props.form.refinements.length === 0)
    ? t('configurator.form.placeholders.please_select_surface_first')
    : t('configurator.form.placeholders.please_select')
})

const printModeOptions = computed(() => {
  if (props.form.article === null) {
    return []
  }

  if (props.form.is_logo_print) {
    return [
      { name: t('configurator.form.labels.print_per_surface'), value: false },
      { name: t('configurator.form.labels.print_across_all_surfaces'), value: true },
    ]
  }
  return [
    { name: t('configurator.form.labels.lettering_per_surface'), value: false },
    { name: t('configurator.form.labels.lettering_across_all_surfaces'), value: true },
  ]
})
// define watch
watch(() => props.form.article, () => {
  if (props.form.article === null || props.form.refinements.length === 0) {
    props.form.is_logo_print = false

    return
  }

  if(props.form.refinements.length > 1) {
    for (let i = 0; i < props.form.refinements.length; i++) {
      let ref = props.form.refinements[i]
      const index = ref.articles.findIndex((article) => {
        return article.article_id === props.form.article.article.id
      })
      if (index > -1) {
        props.form.is_logo_print = props.form.is_logo_print ? 1 : (ref.articles[index].article.type === ARTICLE_TYPE_PRINT)
      }
    }
  } else if(props.form.refinements.length > 0) {
    const index = props.form.refinements[0].articles.findIndex((article) => {
      return article.article_id === props.form.article.article.id
    })

    if (index > -1) {
      props.form.is_logo_print = (props.form.refinements[0].articles[index].article.type === ARTICLE_TYPE_PRINT)
    }
  }

})
watch(() => props.form.refinements, () => {
  props.form.article = null
  if (props.form.refinements.length < 2) {
    props.form.is_seamless_print = false
  }
})

watch(() => props.form.is_seamless_print, (current, previous) => {
  if (current !== previous) {
    props.form.article = null
  }
})
// define methods
const close = () => emit('close')

const submit = async () => {
  props.form.errors.clear()

  if (props.form.refinements.length === 0) {
    props.form.errors.record({ errors: {
      'booth_refinement': t('configurator.form.errors.graphic_design'),
    }})

    return
  }
  if (props.form.article === null) {
    props.form.errors.record({ errors: {
      'article': t('configurator.form.errors.print_article'),
    }})

    return
  }

  if (!props.form.is_logo_print && props.form.label.trim().length === 0) {
    props.form.errors.record({ errors: {
      'label': t('configurator.form.errors.lettering'),
    }})

    return
  }

  if (dropzoneFile.value !== '') {
    await upload()
  }

  if (props.form.refinements.length === 1) {
    if (props.form.is_logo_print) {
      props.form.spanning_mode = { name: t('configurator.form.labels.print_per_surface'), value: false }
    } else {
      props.form.spanning_mode = { name: t('configurator.form.labels.lettering_per_surface'), value: false }
    }
  }

  dropzoneFile.value = ''
  emit('submit')
}

const upload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.upload'))
  uploadForm.file = dropzoneFile.value
  await uploadForm.post('/' + exhibition.value + '/upload')
    .then(response => {
      uploadForm.resetToOriginal()
      props.form.file = response.data.file
      hideLoadingIndicator()
    })
    .catch(err => {
      if (err !== undefined && err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(t('errors.please_try_again'), t('toast.title.error'))
        logger.error('upload error', err, (err === undefined) ? '' : err.toString())
      }
    })
}

const customLabel = ({name, description}) => {
  return '${title} - ${description}'
}

const selectSpanningMode = () => {
  let spanningModeKey;
  if (props.form.is_seamless_print) {
    spanningModeKey = props.form.is_logo_print ? 'print_across_all_surfaces' : 'lettering_across_all_surfaces';
  } else {
    spanningModeKey = props.form.is_logo_print ? 'print_per_surface' : 'lettering_per_surface';
  }
  props.form.spanning_mode = { name: t(`configurator.form.labels.${spanningModeKey}`), value: true };
}

onMounted(() => {
  props.form.spanning_mode = (printModeOptions.value.length > 0) ? printModeOptions.value[0] : null
  if (props.mandatoryMode && props.availableRefinements.length === 1) {
    props.form.refinements.push(props.availableRefinements[0])
  }
})

// define watch
watch(
  [() => props.form.is_seamless_print, () => props.form.is_logo_print], () => {
    selectSpanningMode();
  }
);
</script>